import { useState } from "react";

function ContactPage() {
  // ──────────────────────────────────────────────────────────
  //  STATE
  // ──────────────────────────────────────────────────────────
  const [phoneConsent, setPhoneConsent]   = useState(false);
  const [termsConsent, setTermsConsent]   = useState(false);
  const [phone,        setPhone]          = useState("");
  const [isSubmitting, setIsSubmitting]   = useState(false);
  const [isSubmitted,  setIsSubmitted]    = useState(false);
  const [formData,     setFormData]       = useState({
    name:    "",
    email:   "",
    phone:   "",
    message: "",
  });

  // ──────────────────────────────────────────────────────────
  //  HANDLERS
  // ──────────────────────────────────────────────────────────
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    if (name === "phone") setPhone(value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Basic client-side compliance checks
    if (phone.trim() && !phoneConsent) {
      alert("Please consent to receive text messages to continue.");
      return;
    }
    if (!termsConsent) {
      alert("Please agree to the Terms and Conditions to continue.");
      return;
    }

    setIsSubmitting(true);
    try {
      const body = new URLSearchParams();
      Object.entries(formData).forEach(([k, v]) => body.append(k, v));
      body.append("phoneConsent", phoneConsent.toString());
      body.append("termsConsent", termsConsent.toString());

      const res = await fetch("https://api.southcoaststrategy.com", {
        method: "POST",
        body,
        headers: {
          "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
        },
      });

      if (res.ok) {
        setIsSubmitted(true);
        setFormData({ name: "", email: "", phone: "", message: "" });
        setPhone("");
        setPhoneConsent(false);
        setTermsConsent(false);
      } else {
        alert("There was an error submitting your message. Please try again later.");
      }
    } catch (err: any) {
      alert("An error occurred: " + err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isSubmitDisabled =
    (!!phone.trim() && !phoneConsent) || !termsConsent || isSubmitting;

  // ──────────────────────────────────────────────────────────
  //  RENDER
  // ──────────────────────────────────────────────────────────
  return (
    <main className="bg-white py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Title */}
        <h1
          className="text-4xl font-bold text-center mb-12 text-black"
          style={{ fontFamily: "Poppins, sans-serif" }}
        >
          Contact Bay Area Conservatives PAC
        </h1>

        {/* Form */}
        <div className="max-w-2xl mx-auto mb-16">
          {!isSubmitted ? (
            <div className="bg-gray-100 p-8 rounded-lg shadow">
              <h2 className="text-2xl font-bold mb-6 text-black">
                We’d love to hear from you!
              </h2>

              <form className="space-y-6" onSubmit={handleSubmit} noValidate>
                {/* Name */}
                <div>
                  <label htmlFor="name" className="block text-lg font-medium text-black mb-1">
                    Name
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-900 text-black"
                  />
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-lg font-medium text-black mb-1">
                    Email&nbsp;<span className="text-red-600">*</span>
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-900 text-black"
                  />
                </div>

                {/* Phone */}
                <div>
                  <label htmlFor="phone" className="block text-lg font-medium text-black mb-1">
                    Phone&nbsp;<span className="text-gray-500">(optional)</span>
                  </label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-900 text-black"
                  />
                </div>

                {/* Message */}
                <div>
                  <label htmlFor="message" className="block text-lg font-medium text-black mb-1">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={4}
                    value={formData.message}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-900 text-black"
                  />
                </div>

                {/* SMS consent */}
                <div className="flex items-start">
                  <input
                    id="text-consent"
                    name="text-consent"
                    type="checkbox"
                    checked={phoneConsent}
                    onChange={() => setPhoneConsent(!phoneConsent)}
                    className="h-5 w-5 text-indigo-900 focus:ring-indigo-900 border-gray-300 rounded"
                  />
                  <label htmlFor="text-consent" className="ml-3 text-black">
                    I consent to receive <strong>political and marketing text messages</strong> from
                    Bay Area Conservatives PAC at the number provided.
                  </label>
                </div>

                {/* CTIA/Telnyx-compliant micro-copy */}
                <div className="bg-gray-200 p-4 rounded-md text-black">
                  <div style={{ fontSize: "0.75rem", lineHeight: "1.25" }}>
                    <p style={{ fontWeight: "bold", marginBottom: "0.25rem" }}>
                      Text Message Opt-In Disclosure
                    </p>
                    <p style={{ marginBottom: "0.25rem" }}>
                      By providing your phone number and clicking “Submit,” you consent to receive
                      <em> recurring, manually-sent political and marketing text messages</em> from
                      Bay Area Conservatives PAC. Message frequency varies. Standard message &amp;
                      data rates may apply. Reply <strong>STOP</strong> to opt out, <strong>HELP</strong> for help.
                    </p>
                    <p style={{ marginBottom: "0.25rem" }}>
                      <strong>Donations will not be solicited. Marketing messages will be sent via text.</strong>
                      {/* If you truly will NOT solicit donations by SMS, delete
                          the line above and this comment. */}
                    </p>
                    <p style={{ marginBottom: 0 }}>
                      Consent is not a condition of purchase. See our&nbsp;
                      <a
                        href="/privacy-policy"
                        className="text-indigo-700 hover:text-indigo-900 underline"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Privacy Policy
                      </a>{" "}
                      and&nbsp;
                      <a
                        href="/terms-and-conditions"
                        className="text-indigo-700 hover:text-indigo-900 underline"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Terms & Conditions
                      </a>
                      .
                    </p>
                  </div>
                </div>

                {/* Terms consent */}
                <div className="flex items-start">
                  <input
                    id="terms-consent"
                    name="terms-consent"
                    type="checkbox"
                    checked={termsConsent}
                    onChange={() => setTermsConsent(!termsConsent)}
                    required
                    className="h-5 w-5 text-indigo-900 focus:ring-indigo-900 border-gray-300 rounded"
                  />
                  <label htmlFor="terms-consent" className="ml-3 text-black">
                    I agree to the&nbsp;
                    <a
                      href="/terms-and-conditions"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-indigo-700 hover:text-indigo-900 underline"
                    >
                      Terms & Conditions
                    </a>{" "}
                    and&nbsp;
                    <a
                      href="/privacy-policy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-indigo-700 hover:text-indigo-900 underline"
                    >
                      Privacy Policy
                    </a>
                    .
                  </label>
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={isSubmitDisabled}
                  className={`font-bold py-3 px-8 rounded-full shadow-lg transition ${
                    isSubmitDisabled
                      ? "bg-gray-400 text-gray-200 cursor-not-allowed"
                      : "bg-black text-white hover:bg-indigo-800"
                  }`}
                >
                  {isSubmitting ? "Sending…" : "Send"}
                </button>

                {/* reCAPTCHA notice */}
                <p className="text-xs text-gray-600 mt-4">
                  This site is protected by reCAPTCHA and the Google&nbsp;
                  <a
                    href="https://policies.google.com/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-indigo-700 hover:text-indigo-900 underline"
                  >
                    Privacy Policy
                  </a>{" "}
                  and&nbsp;
                  <a
                    href="https://policies.google.com/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-indigo-700 hover:text-indigo-900 underline"
                  >
                    Terms of Service
                  </a>{" "}
                  apply.
                </p>
              </form>
            </div>
          ) : (
            /* Success state */
            <div className="bg-gray-100 p-8 rounded-lg shadow text-center">
              <svg
                className="mx-auto h-12 w-12 text-green-600 mb-4"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <h3 className="text-2xl font-bold mb-2 text-black">Message Sent!</h3>
              <p className="text-black">Thank you for reaching out — we’ll be in touch shortly.</p>
              <button
                onClick={() => setIsSubmitted(false)}
                className="mt-6 bg-black text-white font-bold py-2 px-6 rounded-full hover:bg-indigo-800 transition"
              >
                Send Another Message
              </button>
            </div>
          )}
        </div>

        {/* Contact details & donate CTA */}
        <div className="max-w-2xl mx-auto mb-16 bg-gray-100 p-8 rounded-lg shadow">
          <h4 className="text-2xl font-bold mb-6 text-black">Bay Area Conservatives PAC</h4>

          <div className="space-y-4">
            <div className="flex items-center space-x-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-indigo-900" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <span className="text-lg text-black">info@bayareaconservativepac.org</span>
            </div>

            <div className="flex items-center space-x-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-indigo-900" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span className="text-lg text-black">Clear Lake & Galveston Bay, TX</span>
            </div>

            <div className="flex items-center space-x-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-indigo-900" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03 8 9 8s9-3.582 9-8z" />
              </svg>
              <a
                href="https://www.facebook.com/BayAreaConservatives"
                target="_blank"
                rel="noopener noreferrer"
                className="text-lg text-black hover:text-indigo-700"
              >
                Facebook @BayAreaConservatives
              </a>
            </div>
          </div>

          <div className="mt-8">
            <h5 className="font-bold text-xl mb-4 text-black">Support Our Work</h5>
            <p className="mb-4 text-black">
              Your generosity enables us to elect principled conservatives across the Bay Area.
            </p>
            <a
              href="/donate"
              className="inline-block bg-black text-white font-bold py-2 px-6 rounded-full hover:bg-indigo-800 transition"
            >
              Donate to BAC PAC
            </a>
          </div>
        </div>
      </div>
    </main>
  );
}

export default ContactPage;
